import type {
  Vacancy,
  VacancyFormData,
  ApiResponse,
  PaginatedResponse,
} from "@shared/types";

// API client for making requests to the backend

const API_BASE = "/api";

interface ApiOptions extends RequestInit {
  params?: Record<string, string>;
}

async function request<T>(endpoint: string, options: ApiOptions = {}): Promise<T> {
  const { params, ...fetchOptions } = options;

  let url = `${API_BASE}${endpoint}`;
  if (params) {
    const searchParams = new URLSearchParams(params);
    url += `?${searchParams.toString()}`;
  }

  const response = await fetch(url, {
    ...fetchOptions,
    credentials: "include",
    headers: {
      "Content-Type": "application/json",
      ...fetchOptions.headers,
    },
  });

  const data = (await response.json()) as T & { error?: string };

  if (!response.ok) {
    throw new Error(data.error || "Request failed");
  }

  return data as T;
}

export const api = {
  get: <T>(endpoint: string, options?: ApiOptions) =>
    request<T>(endpoint, { ...options, method: "GET" }),

  post: <T>(endpoint: string, body: unknown, options?: ApiOptions) =>
    request<T>(endpoint, { ...options, method: "POST", body: JSON.stringify(body) }),

  patch: <T>(endpoint: string, body: unknown, options?: ApiOptions) =>
    request<T>(endpoint, { ...options, method: "PATCH", body: JSON.stringify(body) }),

  delete: <T>(endpoint: string, options?: ApiOptions) =>
    request<T>(endpoint, { ...options, method: "DELETE" }),
};

// Type-safe API methods
export const apiClient = {
  // Public endpoints
  vacancies: {
    list: () => api.get<{ success: boolean; data: unknown[] }>("/vacancies"),
  },
  applications: {
    submit: (data: unknown) =>
      api.post<{ success: boolean; message: string; data: { id: number } }>(
        "/applications",
        data
      ),
  },
  contact: {
    submit: (data: unknown) =>
      api.post<{ success: boolean; message: string; data: { id: number } }>(
        "/contact",
        data
      ),
  },

  // Admin endpoints
  admin: {
    login: (email: string, password: string) =>
      api.post<{ success: boolean; data: { user: unknown } }>("/admin/login", {
        email,
        password,
      }),
    logout: () => api.post<{ success: boolean }>("/admin/logout", {}),
    me: () =>
      api.get<{ success: boolean; data: { user: unknown } }>("/admin/me"),
    stats: () =>
      api.get<{ success: boolean; data: unknown }>("/admin/stats"),
    features: () =>
      api.get<ApiResponse<{
        vacancyManagement: boolean;
        applicationManagement: boolean;
        contactManagement: boolean;
      }>>("/admin/features"),
    vacancies: {
      list: (params?: { status?: string; page?: string; pageSize?: string }) =>
        api.get<PaginatedResponse<Vacancy>>("/admin/vacancies", { params }),
      get: (id: number) =>
        api.get<ApiResponse<Vacancy>>(`/admin/vacancies/${id}`),
      create: (data: VacancyFormData) =>
        api.post<ApiResponse<Vacancy>>("/admin/vacancies", data),
      update: (id: number, data: Partial<VacancyFormData>) =>
        api.patch<ApiResponse<Vacancy>>(`/admin/vacancies/${id}`, data),
      delete: (id: number) =>
        api.delete<ApiResponse>(`/admin/vacancies/${id}`),
    },
    applications: {
      list: (params?: { status?: string; page?: string; pageSize?: string }) =>
        api.get<{
          success: boolean;
          data: unknown[];
          total: number;
          page: number;
          pageSize: number;
          totalPages: number;
        }>("/admin/applications", { params }),
      get: (id: number) =>
        api.get<{ success: boolean; data: unknown }>(`/admin/applications/${id}`),
      update: (id: number, data: { status?: string; notes?: string }) =>
        api.patch<{ success: boolean }>(`/admin/applications/${id}`, data),
    },
    contacts: {
      list: (params?: { status?: string; page?: string; pageSize?: string }) =>
        api.get<{
          success: boolean;
          data: unknown[];
          total: number;
          page: number;
          pageSize: number;
          totalPages: number;
        }>("/admin/contacts", { params }),
      update: (id: number, data: { status?: string; notes?: string }) =>
        api.patch<{ success: boolean }>(`/admin/contacts/${id}`, data),
    },
  },
};
